import { gql } from "lib/apolloClient";
import {
  courseAssignmentFragment,
  assignmentFragment,
  studentAssignmentFragment,
  organizationAssignmentFragment,
  courseUserMapFragment,
  assignmentSummaryCountsFragment,
  studentDetailsFragment,
  tasksStateWiseCountFragment,
  standardSetRatingFragment,
} from "./ClassRoomFragments";
import {
  resolvedFieldValueFragment,
  studentFragment,
  toolResponseFragment,
  studentsAlertFragment,
} from "modules/CommonFragments";

export const getAssignmentFilterSchoolsDataQuery = gql`
  query getAssignmentFilterSchoolsData(
    $organizationId: ID!
    $curriculumIds: [ID!]
    $gradingPeriodsFilters: TermFilters!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumIds) {
          id
          label
          acronym
          logo
          gradingPeriods(filters: $gradingPeriodsFilters) {
            edges {
              node {
                id
                label
                startDate
                endDate
                type
              }
            }
          }
        }
      }
    }
  }
`;

export const getAssignmentFoldersAndPostSettingsQuery = gql`
  query getFolderAndPostsSettings($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        folders {
          id
          name
        }
        content {
          id
          ... on Assessment {
            id
            isIncludedInCourseFlow
          }
        }
        isStudentSubmissionEnabled
        isClassDiscussionEnabled
        isSimilarityCheckEnabled
        isPrivate
        isExcludedFromFinalGradeCalculation
        isSharedWithClassMaterial
        deadline
        isLockSubmissionEnabled
        isTimedTestEnabled
        isLockScreenEnabled
      }
    }
  }
`;

export const getCourseAssignmentFeedQuery = gql`
  query getCourseAssignmentFeed(
    $courseId: ID!
    $createdBy: ID
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $searchText: String
    $after: String
    $states: [ASSIGNMENT_STATE_ENUM!]
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $folderIds: [ID!]
    $studentIds: [ID!]
    $showUpcoming: Boolean
    $academicTermIds: [ID!]
    $assessmentTypes: [String!]
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $first: Int
    $excludedAssessmentTypes: [String!]
    $isFetchUnreadConverstionAssignment: Boolean
    $isFetchToReviewAssigment: Boolean
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseAssignmentItem
      }
    }
  }
  ${courseAssignmentFragment.courseAssignmentFeed}
`;

export const getCourseAssignmentFeedWithoutAssignedStudentQuery = gql`
  query getCourseAssignmentFeed(
    $courseId: ID!
    $createdBy: ID
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $searchText: String
    $after: String
    $states: [ASSIGNMENT_STATE_ENUM!]
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $folderIds: [ID!]
    $studentIds: [ID!]
    $academicTermIds: [ID!]
    $showUpcoming: Boolean
    $assessmentTypes: [String!]
    $first: Int
    $excludedAssessmentTypes: [String!]
    $isFetchUnreadConverstionAssignment: Boolean
    $isFetchToReviewAssigment: Boolean
    $createdByIds: [ID!]
    $isFetchToReviewAssigmentV2: Boolean
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseAssignmentItem
      }
    }
  }
  ${courseAssignmentFragment.courseAssignmentFeedWithoutAssignedStudent}
`;

export const getDeletedAssignmentFeedQuery = gql`
  query getDeletedAssignmentFeed(
    $courseId: ID!
    $after: String
    $first: Int
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $excludedAssessmentTypes: [String!]
    $isFetchDeletedAssignments: Boolean
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseAssignmentItem
      }
    }
  }
  ${courseAssignmentFragment.courseDeletedAssignmentFeed}
`;

export const getTeacherReviewFilterCountQuery = gql`
  query getTeacherReviewFilterCount(
    $organizationId: ID!
    $states: [ASSIGNMENT_STATE_ENUM!]
    $courseIds: [ID!]
    $createdBy: ID
    $searchText: String
    $lastWeekStartDate: String
    $lastWeekEndDate: String
    $earlierEndDate: String
    $thisWeekStartDate: String
    $thisWeekEndDate: String
    $archiveLastWeekStartDate: String
    $archiveLastWeekEndDate: String
    $archiveEarlierEndDate: String
    $archiveThisWeekStartDate: String
    $archiveThisWeekEndDate: String
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $excludedAssessmentTypes: [String!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...teacherReviewFilterCountItem
      }
    }
  }
  ${courseAssignmentFragment.teacherReviewFilterCount}
`;

export const getTeacherClassroomReviewQuery = gql`
  query getTeacherClassroomReview(
    $organizationId: ID!
    $courseIds: [ID!]
    $createdBy: ID
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $searchText: String
    $after: String
    $states: [ASSIGNMENT_STATE_ENUM!]
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $studentIds: [ID!]
    $showUpcoming: Boolean
    $deadlineDates: GenericDateRange
    $archiveDates: GenericDateRange
    $isNoDeadlineDate: Boolean
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $excludedAssessmentTypes: [String!]
    $isFetchUnreadConverstionAssignment: Boolean
    $isFetchToReviewAssigment: Boolean
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...courseAssignmentItem
      }
    }
  }
  ${courseAssignmentFragment.teacherClassroomReview}
`;

export const getCourseAssignmentStateWiseCountQuery = gql`
  query getCourseAssignmentStateWiseCount(
    $courseId: ID!
    $excludedAssessmentTypes: [String!]
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseAssignmentItem
      }
    }
  }
  ${courseAssignmentFragment.courseAssignmentStateWiseCount}
`;

export const getCourseAssignmentStateWiseCountV2Query = gql`
  query getCourseAssignmentStateWiseCountV2(
    $courseId: ID!
    $filters: AssignmentFeedFilters
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseAssignmentItem
      }
    }
  }
  ${courseAssignmentFragment.courseAssignmentStateWiseCountV2}
`;

export const getOrganizationAssignmentStateWiseCountV2Query = gql`
  query getOrganizationAssignmentStateWiseCountV2(
    $organizationId: ID!
    $filters: AssignmentFeedFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationAssignmentItem
      }
    }
  }
  ${organizationAssignmentFragment.organizationAssignmentStateWiseCountV2}
`;

export const getParentStudentCourseAssignmentStateWiseCountQuery = gql`
  query getCourseAssignmentStateWiseCount(
    $userId: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: StudentAssignmentFeedFilters
    $studentIds: [ID!]
  ) {
    node(id: $userId, type: $type) {
      id
      ... on FamilyMember {
        id
        assignments(filters: $filters, studentIds: $studentIds) {
          stateWiseCount {
            state
            count
          }
        }
      }
      ... on Student {
        id
        assignments(filters: $filters) {
          stateWiseCount {
            state
            count
          }
        }
      }
    }
  }
`;

export const getAssignmentEditDetailsQuery = gql`
  query getAssignmentEditDetails(
    $id: ID!
    $userArchivedFilters: UserArchivedFilters
    $isLinkedAssignments: Boolean! = false
  ) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        ...assignmentItem
      }
    }
  }
  ${assignmentFragment.assignmentEdit}
`;

export const getSourceAssignmentDetailsQuery = gql`
  query getSourceAssignmentDetails($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        state {
          state
          publishedAt
        }
        deadline
        closeSubmissionDate
        visibility
        isStudentSubmissionEnabled
        isClassDiscussionEnabled
        isExcludedFromFinalGradeCalculation
        isSharedWithClassMaterial
        isTimedTestEnabled
        isFixedStartTime
        isLockScreenEnabled
        isSafeBrowserEnabled
        timedTestDuration
        isSimilarityCheckEnabled
      }
    }
  }
`;

export const getStudentCourseTagsQuery = gql`
  query getStudentCourseTags($id: ID!, $courseIds: [ID!]) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        assignedStudents {
          ... on StudentAssignment {
            id
            student {
              ...studentItem
            }
          }
        }
      }
    }
  }
  ${studentFragment.studentCourseMap}
`;

export const getAssignmentDetailsQuery = gql`
  query getAssignmentDetails(
    $id: ID!
    $studentIds: [ID!]
    $showOnlySubmitted: Boolean
    $userFilter: TOOL_RESPONSE_USER_FILTER_ENUM
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $entityEventsFilter: EntityEventsFilter
    $organizationResourcesOrderBy: ORDER_BY_ENUM
    $organizationResourcesOrderByDirection: ORDER_BY_DIRECTION
    $organizationResourcesFirst: Int
    $organizationResourceFilters: OrganizationResourceFilter
    $userArchivedFilters: UserArchivedFilters
    $fetchAiUsageMetrics: Boolean = false
  ) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        ...assignmentItem
      }
    }
  }
  ${assignmentFragment.assignmentDetails}
`;

export const getAssignmentBasicDetailsQuery = gql`
  query getAssignmentBasicDetails($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        course {
          id
          learningCourse {
            id
          }
        }
        content {
          id
          ... on Assessment {
            id
            title {
              id
              value
            }
            assessmentType {
              id
              value
            }
            taskType {
              id
              type
              label
            }
            category {
              id
              label
              colorCode
            }
            assignments {
              userAssignmentEdges {
                id
                course {
                  id
                  title
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getAssignmentDeletedStatusQuery = gql`
  query getAssignmentDeletedStatus($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        ...assignmentItem
      }
    }
  }
  ${assignmentFragment.assignmentDeletedStatus}
`;

export const getStudentAssignmentDetailsQuery = gql`
  query getStudentAssignmentDetails(
    $id: ID!
    $showOnlySubmitted: Boolean
    $userFilter: TOOL_RESPONSE_USER_FILTER_ENUM
    $entityEventsFilter: EntityEventsFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        ...studentAssignmentItem
      }
    }
  }
  ${studentAssignmentFragment.studentAssignmentDetails}
`;

export const getStudentAiEvaluationJobStatusQuery = gql`
  query getStudentAssignmentDetails($id: ID!) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        id
        aiEvaluationJob(filters: { isFetchLatestJob: true }) {
          id
          status
          jobStartExecutionTime
          metadata
        }
        submission {
          id
          submittedAt
        }
      }
    }
  }
`;

export const getStudentTodosQuery = gql`
  query getStudentTodos(
    $studentId: ID!
    $courseIds: [ID!]
    $searchText: String
    $states: [ASSIGNMENT_STATE_ENUM!]
    $status: [STUDENT_ASSIGNMENT_STATUS_ENUM!]
    $submissionStatus: [STUDENT_ASSIGNMENT_SUBMISSION_ENUM_V2!]
    $first: Int = 10
    $after: String
    $deadlineDates: GenericDateRange
    $archiveDates: GenericDateRange
    $isNoDeadlineDate: Boolean
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $assessmentTypes: [String!]
    $folderIds: [ID!]
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $academicYears: [ID!]
    $excludedAssessmentTypes: [String!]
    $isFetchUnreadConverstionAssignment: Boolean
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $isEvaluationApplicable: Boolean
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        ...studentTodosItem
      }
    }
  }
  ${courseAssignmentFragment.studentTodos}
`;

export const getFamilyStudentTodosQuery = gql`
  query getStudentTodos(
    $userId: ID!
    $courseIds: [ID!]
    $studentIds: [ID!]
    $searchText: String
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $assessmentTypes: [String!]
    $states: [ASSIGNMENT_STATE_ENUM!]
    $status: [STUDENT_ASSIGNMENT_STATUS_ENUM!]
    $submissionStatus: [STUDENT_ASSIGNMENT_SUBMISSION_ENUM_V2!]
    $after: String
    $deadlineDates: GenericDateRange
    $archiveDates: GenericDateRange
    $isNoDeadlineDate: Boolean
    $folderIds: [ID!]
    $first: Int
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $academicYears: [ID!]
    $isFetchUnreadConverstionAssignment: Boolean
    $unreadMessageCountFilters: UnreadMessageCountFilters
    $isEvaluationApplicable: Boolean
  ) {
    node(id: $userId, type: FAMILY_MEMBER) {
      id
      ... on FamilyMember {
        ...familyStudentTodosItem
      }
    }
  }
  ${courseAssignmentFragment.familyStudentTodos}
`;

export const getStudentTodosFilterCountQuery = gql`
  query getStudentFilterCount(
    $courseIds: [ID!]
    $studentId: ID!
    $states: [ASSIGNMENT_STATE_ENUM!]
    $status: [STUDENT_ASSIGNMENT_STATUS_ENUM!]
    $lastWeekStartDate: String
    $lastWeekEndDate: String
    $earlierEndDate: String
    $thisWeekStartDate: String
    $thisWeekEndDate: String
    $laterStartDate: String
    $archiveLastWeekStartDate: String
    $archiveLastWeekEndDate: String
    $archiveEarlierEndDate: String
    $archiveThisWeekStartDate: String
    $archiveThisWeekEndDate: String
    $archiveLaterStartDate: String
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $academicYears: [ID!]
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        ...studentTodosFilterCountItem
      }
    }
  }
  ${courseAssignmentFragment.studentTodosFilterCount}
`;

export const getFamilyStudentTodosFilterCountQuery = gql`
  query getStudentFilterCount(
    $userId: ID!
    $studentIds: [ID!]
    $states: [ASSIGNMENT_STATE_ENUM!]
    $status: [STUDENT_ASSIGNMENT_STATUS_ENUM!]
    $lastWeekStartDate: String
    $lastWeekEndDate: String
    $earlierEndDate: String
    $thisWeekStartDate: String
    $thisWeekEndDate: String
    $laterStartDate: String
    $archiveLastWeekStartDate: String
    $archiveLastWeekEndDate: String
    $archiveEarlierEndDate: String
    $archiveThisWeekStartDate: String
    $archiveThisWeekEndDate: String
    $archiveLaterStartDate: String
    $courseIds: [ID!]
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $academicYears: [ID!]
  ) {
    node(id: $userId, type: FAMILY_MEMBER) {
      id
      ... on FamilyMember {
        ...familyStudentTodosFilterCountItem
      }
    }
  }
  ${courseAssignmentFragment.familyStudentTodosFilterCount}
`;

export const getOrganizationAssignmentFeedQuery = gql`
  query getOrganizationAssignmentFeed(
    $organizationId: ID!
    $first: Int
    $after: String
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $filters: AssignmentFeedFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationAssignmentItem
      }
    }
  }
  ${organizationAssignmentFragment.organizationAssignmentFeed}
`;

export const getAssignmentEvaluationInsightQuery = gql`
  query getAssignmentEvaluationInsight(
    $id: ID!
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        ...assignmentEvaluationItem
      }
    }
  }
  ${assignmentFragment.assignmentEvalutionInsight}
`;

export const getCourseUserMapQuery = gql`
  query getCourseUserMap(
    $id: ID!
    $filters: CourseUserMapFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseUserMapFragment.courseUserMap}
`;

export const getMultiCourseUserMapQuery = gql`
  query getMultiCourseUserMap(
    $ids: [ID!]
    $filters: CourseUserMapFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    multiNode(ids: $ids, type: COURSE) {
      id
      ... on Course {
        ...courseItem
        userMap(filters: $filters) {
          edges {
            node {
              id
              subjectWiseLevel {
                level {
                  id
                  label
                  abbreviation
                }
              }
            }
          }
        }
      }
    }
  }
  ${courseUserMapFragment.courseUserMap}
`;

export const getAssignedStudentsQuery = gql`
  query getAssignedStudentsQuery($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        assignedStudents {
          id
          student {
            ...studentItem
            ... on Student {
              id
              alertTypes {
                ...alertTypeFields
              }
            }
          }
          state
          status
          isEvaluated
          isSharedWithStudent
          isEvaluationApplicable
          isSubmitted
          lockingState {
            state
          }
        }
      }
    }
  }
  ${studentFragment.basicStudentDetails}
  ${studentsAlertFragment}
`;

export const getSubjectAndGenericTagByCourseQuery = gql`
  query getSubjectAndGenericTagByCourse($id: ID!) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        subjects(filters: { archivalState: ALL }) {
          id
          name
        }
        genericTags(filters: { types: [SUBJECT_LEVEL] }) {
          id
          label
          type
        }
        subjectLevelMap {
          associatedLevels {
            id
            label
            type
          }
        }
      }
    }
  }
`;

export const getCourseSubjectFlatVariantsLevelQuery = gql`
  query getCourseSubjectFlatVariantsLevel(
    $id: ID!
    $gradeFilters: CourseGradeFilters
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        subjects(filters: { archivalState: ALL }) {
          id
          name
          type
          flatVariants(filters: { archivalState: ALL }) {
            level {
              id
              label
            }
          }
        }
        grade(filters: $gradeFilters) {
          id
        }
      }
    }
  }
`;

export const getLearningCourseSubjectFlatVariantsLevelQuery = gql`
  query getLearningCourseSubjectFlatVariantsLevel($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        subjects {
          id
          name
          type
          flatVariants {
            level {
              id
              label
            }
          }
        }
        grades {
          id
        }
      }
    }
  }
`;

export const getUnitPlanFieldsQuery = gql`
  query getUnitPlanFields($id: ID!) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        id
        templateId
        fields(uids: ["unitPlan"]) {
          id
          uid
          value
          resolvedMinimalTree {
            ... on ResolvedFieldUnitPlan {
              id
              type
              unitPlan {
                id
                fields(
                  uids: ["objectivesMYP", "interdisciplinaryCriteriaMYP"]
                ) {
                  id
                  uid
                  value
                  resolvedMinimalTree {
                    ...resolvedMinimalTreeItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${resolvedFieldValueFragment.resolvedMinimalTree}
`;

export const getCategoryScoreValuesQuery = gql`
  query getCategoryScoreValues($id: ID!) {
    node(id: $id, type: ACADEMIC_CRITERIA_VALUE) {
      id
      ... on AcademicCriteriaValue {
        scoreValues {
          id
          value
          genericTag {
            id
            label
          }
        }
      }
    }
  }
`;

export const downloadInsightsDataQuery = gql`
  query downloadInsightsData($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        toolInsightsDownload
      }
    }
  }
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: CourseFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Student {
        id
        courses(filters: $filters) {
          id
          title
          isRemovedCourseForLoggedInUser
        }
      }
    }
  }
`;

export const getUserCoursesV2Query = gql`
  query getUserCoursesV2(
    $id: ID!
    $filters: CourseFilter
    $enabledModulesFilters: EnabledModulesFiltersInput
    $aclControlParameterFilter: ACLControlParameterFilter
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          totalCount
          edges {
            node {
              academicYears {
                id
                startDate
                endDate
                terms(
                  filters: { curriculumProgramIds: $curriculumProgramIds }
                ) {
                  id
                  label
                  type
                  endDate
                  startDate
                }
              }
              id
              title
              profileImageData {
                url
                icon
              }
              curriculumProgram {
                id
              }
              enabledModules(filters: $enabledModulesFilters)
              aclControlParameters(filters: $aclControlParameterFilter) {
                id
                uid
                permission
                value
              }
              learningCourse {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getStaffCoursesPaginatedQuery = gql`
  query getStaffCoursesPaginated(
    $id: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
    $orderBy: ORDER_BY_ENUM
    $secondOrderByKey: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $enabledModulesFilters: EnabledModulesFiltersInput
    $isRnPConsumptionEnabled: Boolean!
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        coursesV2(
          first: $first
          after: $after
          filters: $filters
          orderBy: $orderBy
          secondOrderByKey: $secondOrderByKey
          orderByDirection: $orderByDirection
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              profileImageData {
                url
                icon
              }
              curriculumProgram {
                id
              }
              enabledModules(filters: $enabledModulesFilters)
                @skip(if: $isRnPConsumptionEnabled)
              learningCourse {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentAssignmentDetailsQueryV2 = gql`
  query getStudentAssignmentDetailsV2(
    $id: ID!
    $showOnlySubmitted: Boolean
    $userFilter: TOOL_RESPONSE_USER_FILTER_ENUM
    $entityEventsFilter: EntityEventsFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        ...studentAssignmentItem
      }
    }
  }
  ${studentAssignmentFragment.studentAssignmentDetailsV2}
`;

export const getStudentAssignmentQuery = gql`
  query getStudentAssignment(
    $id: ID!
    $userFilter: TOOL_RESPONSE_USER_FILTER_ENUM
    $entityEventsFilter: EntityEventsFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: STUDENT_ASSIGNMENT) {
      id
      ... on StudentAssignment {
        ...studentAssignmentItem
      }
    }
  }
  ${studentAssignmentFragment.studentAssignment}
`;

export const getAssignedStudentsAIConversationQuery = gql`
  query getAssignedStudentsAIConversation($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      ... on Assignment {
        id
        assignedStudents {
          id
          isSubmitted
          aiConversation {
            id
            messages {
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const getAssignedStudentsForAIEvaluationQuery = gql`
  query getAssignedStudentsForAIEvaluation($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      ... on Assignment {
        id
        assignedStudents {
          id
          isSubmitted
          isEvaluated
          aiConversation {
            id
            metadata
            analysisToolEvaluation(filters: { userFilter: ALL }) {
              id
              toolResponses {
                ...toolResponseItem
              }
            }
            messages {
              totalCount
            }
          }
        }
      }
    }
  }
  ${toolResponseFragment.toolResponse}
`;

export const getTeacherTasksQuery = gql`
  query getTeacherTasks(
    $userId: ID!
    $studentIds: [ID!]
    $filters: TaskFilter
    $first: Int
    $after: String
    $orderByDirection: ORDER_BY_DIRECTION
    $orderBy: TODOS_ORDER_BY_ENUM
  ) {
    node(id: $userId, type: STAFF) {
      id
      ... on Staff {
        id
        tasks(
          filters: $filters
          first: $first
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
        ) {
          statusWiseCount {
            category
            count
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          totalCount
          edges {
            id
            orderDate
            itemType
            item {
              id
              ... on Assignment {
                ...assignmentItem
              }
            }
          }
        }
      }
    }
  }
  ${assignmentFragment.assignmentFeedWithoutAssignedStudents}
`;

export const getAssignmentSummaryCountQuery = gql`
  query getAssignmentSummaryCount($id: ID!, $studentIds: [ID!]) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        ...assignmentSummaryCounts
      }
    }
  }
  ${assignmentSummaryCountsFragment.assignmentSummaryCounts}
`;

export const getLearningCourseDetailsQuery = gql`
  query getLearningCourseDetails(
    $learningCourseId: ID!
    $userArchivedFilters: UserArchivedFilters
    $learningCourseClassFilter: LearningCourseClassFilter
  ) {
    node(id: $learningCourseId, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        classes(filters: $learningCourseClassFilter) {
          id
          title
          profileImage
          subjects {
            id
            type
            name
            metadata
            __typename
          }
          allStudents {
            edges {
              node {
                ...studentItem
              }
            }
          }
          isArchived
        }
      }
    }
  }
  ${studentDetailsFragment.basicStudentDetails}
`;

export const getUserCoursesV2BasicDetailsQuery = gql`
  query getUserCoursesV2BasicDetails($id: ID!, $filters: CourseFilter) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getLearningCourseStandardsForRatingQuery = gql`
  query getLearningCourseStandardsForRating(
    $id: ID!
    $filters: PlannerElementNodeFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        standardsWrapper(filters: $filters) @skip(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              ...standardSetForRatingItem
            }
          }
        }

        benchmarksWrapper(filters: $filters) @include(if: $isPYPCurriculum) {
          standardsEdge {
            standardSet {
              ...standardSetForRatingItem
            }
          }
        }
      }
    }
  }
  ${standardSetRatingFragment.standardSetForRating}
`;

export const getCurriculumStandardsForRatingQuery = gql`
  query getCurriculumStandardsForRating(
    $id: ID!
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $isPYPCurriculum: Boolean! = false
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets {
          edges {
            node {
              ...standardSetForRatingItem
            }
          }
        }
      }
    }
  }
  ${standardSetRatingFragment.standardSetForRating}
`;

export const getStudentTasksFilterCountQuery = gql`
  query getStudentTasksFilterCount(
    $userId: ID!
    $type: ENTITY_TYPE_ENUM!
    $courseIds: [ID!]
    $entityTypes: [TASK_EVENT_ITEM_TYPE_ENUM!]
    $academicYearId: ID
    $excludedAssessmentTypes: [String!]
    $startDate: String
    $endDate: String
    $searchText: String
    $overDueStartDate: String
    $overDueEndDate: String
    $curriculumProgramIds: [ID!]
    $projectGroupIds: [ID!]
    $studentIds: [ID!]
  ) {
    node(id: $userId, type: $type) {
      id
      ...studentCountItem
      ...parentCountItem
    }
  }
  ${tasksStateWiseCountFragment.studentCountItem}
  ${tasksStateWiseCountFragment.parentCountItem}
`;

export const getStaffListForAssignmentsQuery = gql`
  query getStaffList(
    $organizationId: ID!
    $searchText: String
    $grades: [ID!]
    $courses: [ID!]
    $archivalState: ARCHIVAL_STATE_ENUM
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(
          filters: {
            searchText: $searchText
            grades: $grades
            courses: $courses
            archivalState: $archivalState
            curriculumProgramIds: $curriculumProgramIds
          }
        ) {
          totalCount
          edges {
            node {
              id
              firstName
              middleName
              lastName
              middleName
              profileImage
              preferredName
              prefix
              suffix
              pronouns
              type
            }
          }
        }
      }
    }
  }
`;

export const getStudentTasksQuery = gql`
  query getStudentTasks(
    $userId: ID!
    $studentIds: [ID!]
    $filters: TaskFilter
    $first: Int
    $after: String
    $orderByDirection: ORDER_BY_DIRECTION
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $userId, type: $type) {
      id
      ... on Student {
        id
        tasks(
          filters: $filters
          first: $first
          after: $after
          orderByDirection: $orderByDirection
        ) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          totalCount
          edges {
            id
            orderDate
            itemType
            item {
              id
              ... on StudentAssignment {
                ...studentAssignmentItem
              }
            }
          }
        }
      }
      ... on FamilyMember {
        id
        tasks(
          filters: $filters
          first: $first
          after: $after
          studentIds: $studentIds
          orderByDirection: $orderByDirection
        ) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          totalCount
          edges {
            id
            orderDate
            itemType
            item {
              id
              ... on StudentAssignment {
                ...studentAssignmentItem
              }
            }
          }
        }
      }
    }
  }
  ${studentAssignmentFragment.studentAssignmentBasicDetails}
`;

export const getDataSetQuery = gql`
  query getDatasets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: PlannerElementNodeSetFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(filters: $filters) {
          edges {
            node {
              id
              title
              type
              gradeScales {
                id
                displayName
                gradeScaleEntityDetails {
                  id
                  gradeScaleValues {
                    id
                    label
                    abbreviation
                    displaySequence
                    description
                    color
                    gradeScaleValueScores {
                      id
                      minValue
                      maxValue
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Do not add any extra fields in this query and use it anywhere
// Create a new query if you need more fields
export const getAssessmentLinkedUnitQuery = gql`
  query getAssessmentLinkedUnit($id: ID!) {
    node(id: $id, type: ASSESSMENT) {
      id
      ... on Assessment {
        id
        fields(uids: ["unitPlan"]) {
          id
          uid
          value
        }
      }
    }
  }
`;

// * Use to identify element can be graded or not
export const getPypPlannerElementNodesQuery = gql`
  query getPypPlannerElementNodes(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $elementTypes: [String!]
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          id
          type
          ... on PlannerThemeSet {
            ...themeSetItem
          }
          ... on ATLSet {
            ...atlSetItem
          }
          ... on PlannerLearnerProfileSet {
            ...lpSetItem
          }
          ... on PlannerConceptSet {
            ...conceptSetItem
          }
          ... on ActionSet {
            ...actionSetItem
          }
          ... on PlannerFocusAreaSet {
            ...focusAreaSetItem
          }
        }
      }
    }
  }

  fragment themeSetItem on PlannerThemeSet {
    id
    nodes {
      id
      focusAreas {
        id
      }
    }
  }

  fragment atlSetItem on ATLSet {
    id
    nodes {
      id
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  }
  fragment lpSetItem on PlannerLearnerProfileSet {
    id
    nodes {
      id
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  }

  fragment conceptSetItem on PlannerConceptSet {
    id
    nodes {
      id
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  }

  fragment actionSetItem on ActionSet {
    id
    nodes {
      id
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  }

  fragment focusAreaSetItem on PlannerFocusAreaSet {
    id
  }
`;

// * Use to identify element can be graded or not
export const getPlannerElementNodesQuery = gql`
  query getPlannerElementNodesOfSet($ids: [ID!]!) {
    multiNode(ids: $ids, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        ...plannerElementSetWithNodesItem
      }
    }
  }

  fragment plannerElementSetWithNodesItem on PlannerElementSet {
    id
    type
    nodes {
      ...plannerElementNodeItem
    }
  }

  fragment plannerElementNodeItem on PlannerElementNode {
    id
    dataSetNodeWrapper {
      properties {
        isArchived
        isGraded
      }
    }
  }
`;

export const getToddleDocValueQuery = gql`
  query getToddleDocValue($id: ID!) {
    node(id: $id, type: ATTACHMENT) {
      id
      ... on Attachment {
        id
        toddleDoc {
          value
        }
      }
    }
  }
`;

export const getToddleDocAttachmentQueryV2 = gql`
  query getToddleDocAttachmentQuery(
    $id: ID!
    $filters: ToddleDocPermissionFilter
    $state: TODDLE_DOC_CONVERSATION_STATE_ENUM
  ) {
    node(id: $id, type: ATTACHMENT) {
      id
      ... on Attachment {
        id
        name
        type
        title
        metadata
        parentType
        toddleDoc {
          id
          value
          conversations(state: $state) {
            id
            referenceId
            colorCode
            derivedThreadState
            isResolved
            messages(filters: $filters) {
              id
              type
              state
              authorType
              content
              createdAt
              createdBy {
                id
                firstName
                lastName
                profileImage
              }
            }
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const getAssignmentCurriculumQuery = gql`
  query getAssignmentCurriculum($id: ID!) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        id
        curriculumProgram {
          id
        }
      }
    }
  }
`;
